import * as React from "react";
import { useState } from "react";
import AssignedTreatmentViewer from "./commons/components/AssignedTreatmentViewer";
import CategoryPanel from "./commons/components/CategoryPanel";
import HeaderBar from "./commons/components/HeaderBar";
import CategoryView from "./commons/model/treatment/CategoryView";
import TreatmentAssignment from "./commons/model/treatment/TreatmentAssignment";
import AssignmentEditor from "./commons/components/AssignmentEditor";
import CriteriaGroup from "./commons/model/treatment/CriteriaGroup";
import Treatment from "./commons/model/treatment/Treatment";
import { AvailableEntitiesSettings } from "./commons/model/EntitySettings";
import LoadingSpinner from "./flow/utils/LoadingSpinner";
import { createRoot } from "react-dom/client";

interface CatalogAssignmentViewerProps {
  treatments: Treatment[];
  categories: CategoryView[];
  entities: AvailableEntitiesSettings;
}

export default function CatalogAssignmentViewer(
  catalogAssignmentViewerProps: CatalogAssignmentViewerProps
) {
  const initAssignments = () => {
    return catalogAssignmentViewerProps.treatments.reduce(
      (acc, treatment) => (
        acc.push(
          ...treatment.assignments.map((it) => ({
            treatment,
            criteriaGroup: it.criteriaGroup,
            isAutoAssigned: it.isAutoAssigned,
            applyIfHealthy: it.applyIfHealthy,
            position: it.position,
          }))
        ),
        acc
      ),
      []
    );
  };

  const initCriteriaGroups: () => any[] = () => {
    return assignments
      .filter(
        (assignment: TreatmentAssignment) =>
          !filter || assignment.treatment.name.toLowerCase().indexOf(filter.toLowerCase()) > -1
      )
      .reduce(getAssignmentsByCriteriaGroup, {});
  };

  const initCategoriesOrder = () => {
    return catalogAssignmentViewerProps.categories.reduce(
      (acc, category, i) => ((acc[category.id] = i), acc),
      {}
    );
  };

  const [assignments, setAssignments] = useState<any[]>(initAssignments());
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [criteriaGroupToEdit, setCriteriaGroupToEdit] = useState<CriteriaGroup>();

  const criteriaGroups = initCriteriaGroups();
  const categoriesOrder = initCategoriesOrder();
  const categories = Object.values(
    Object.values(criteriaGroups).reduce(getCriteriaGroupsByCategory, {})
  ).sort((c1: CategoryView, c2: CategoryView) => categoriesOrder[c1.id] - categoriesOrder[c2.id]);

  return (
    <>
      {loading && <LoadingSpinner />}
      {!loading && (
        <div className="catalog-assignment-viewer v-flex no-wrap">
          <HeaderBar
            title={i18n["Page.Catalog.Assignments.Title"]}
            searchConfiguration={{
              value: filter,
              onChange: (filter) => setFilter(filter),
              placeholder: i18n["Page.Catalog.Assignments.Search"],
            }}
            entities={catalogAssignmentViewerProps.entities}
            onChange={() => {
              setLoading(true);
            }}
          />
          <div className="content">
            {categories.length ? (
              categories.map(({ id, label, criteriaGroups }) => (
                <CategoryPanel id={id} label={label} key={id}>
                  <div className="criteria-group-container">
                    {criteriaGroups
                      .sort(
                        (g1: TreatmentAssignment[], g2: TreatmentAssignment[]) =>
                          g1[0].criteriaGroup.order - g2[0].criteriaGroup.order
                      )
                      .map((treatmentAssignmentsGroupedByCriteriaGroup: TreatmentAssignment[]) => (
                        <div
                          key={treatmentAssignmentsGroupedByCriteriaGroup[0].criteriaGroup.id}
                          className="criteria-group"
                        >
                          <div className="title h-flex space-between">
                            <span>
                              {treatmentAssignmentsGroupedByCriteriaGroup[0].criteriaGroup.i18n}
                            </span>
                            {treatmentAssignmentsGroupedByCriteriaGroup[0].criteriaGroup.apiUrl && (
                              <i
                                className="ic_edit clickable"
                                onClick={() =>
                                  setCriteriaGroupToEdit(
                                    treatmentAssignmentsGroupedByCriteriaGroup[0].criteriaGroup
                                  )
                                }
                              />
                            )}
                          </div>
                          <div className="treatments h-flex">
                            {treatmentAssignmentsGroupedByCriteriaGroup
                              .sort(byTreatmentName)
                              .map((assignment) => (
                                <AssignedTreatmentViewer
                                  assignment={assignment}
                                  key={assignment.treatment.uid}
                                />
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </CategoryPanel>
              ))
            ) : (
              <div className="no-result">{i18n["Page.Catalog.Search.NoResult"]}</div>
            )}
          </div>
        </div>
      )}
      {!loading && criteriaGroupToEdit && (
        <AssignmentEditor
          assignments={assignments.filter((it) => it.criteriaGroup.id === criteriaGroupToEdit.id)}
          treatments={catalogAssignmentViewerProps.treatments}
          closeModal={() => setCriteriaGroupToEdit(null)}
          onChange={updateAssignments.bind(this)}
        />
      )}
    </>
  );

  function updateAssignments(newAssignments: TreatmentAssignment[]) {
    const assignmentsWithoutEditedValues = [...assignments].filter(
      (it) => it.criteriaGroup.id !== criteriaGroupToEdit.id
    );
    setAssignments([...assignmentsWithoutEditedValues, ...newAssignments]);
  }

  function byTreatmentName(t1: TreatmentAssignment, t2: TreatmentAssignment) {
    if (t1.position === t2.position) {
      if (t1.treatment.name.toLowerCase() < t2.treatment.name.toLowerCase()) return -1;
      else if (t1.treatment.name.toLowerCase() > t2.treatment.name.toLowerCase()) return 1;
      else return 0;
    } else return t1.position - t2.position;
  }

  function getAssignmentsByCriteriaGroup(acc, assignment: TreatmentAssignment) {
    if (!acc[assignment.criteriaGroup.id]) acc[assignment.criteriaGroup.id] = [];
    acc[assignment.criteriaGroup.id].push(assignment);
    return acc;
  }

  function getCriteriaGroupsByCategory(acc, criteriaGroup: TreatmentAssignment[]) {
    if (criteriaGroup[0]) {
      const categoryId = criteriaGroup[0].criteriaGroup.categoryId;
      if (!acc[categoryId]) {
        acc[categoryId] = catalogAssignmentViewerProps.categories.find((c) => c.id === categoryId);
        acc[categoryId]["criteriaGroups"] = [];
      }
      acc[categoryId]["criteriaGroups"].push(criteriaGroup);
    }
    return acc;
  }
}

CatalogAssignmentViewer.loadComponent = (
  elementId: string,
  props: CatalogAssignmentViewerProps
) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<CatalogAssignmentViewer {...props} />);
};
